import { PublicClientApplication } from "@azure/msal-browser";
import { createNativeErrorExperience, NativeAuthController } from "./native-auth.js";

const config = window.beerStoreConfig;
const authConfigured = Boolean(config.clientId && config.tenantId && config.tenantSubdomain);
const apiScope = authConfigured ? `api://${config.clientId}/access_as_user` : "";
const authorityHost = config.authorityHost || `${config.tenantSubdomain}.ciamlogin.com`;
const passkeyManagementUrl = config.passkeyManagementUrl
  ? new URL(config.passkeyManagementUrl)
  : null;
const isPasskeyManagementOrigin = passkeyManagementUrl?.origin === window.location.origin;
const passkeyJourney = new URLSearchParams(window.location.search).get("passkey");
const hostedRedirectUri = `${window.location.origin}/redirect.html`;
const passkeyMfaAuthContext = config.passkeyMfaAuthContext || "c1";
const authorityMetadata = JSON.stringify({
  issuer: `https://${config.tenantId}.ciamlogin.com/${config.tenantId}/v2.0`,
  authorization_endpoint: `https://${authorityHost}/${config.tenantId}/oauth2/v2.0/authorize`,
  token_endpoint: `https://${authorityHost}/${config.tenantId}/oauth2/v2.0/token`,
  jwks_uri: `https://${authorityHost}/${config.tenantId}/discovery/v2.0/keys`,
  end_session_endpoint: `https://${authorityHost}/${config.tenantId}/oauth2/v2.0/logout`
});
const passkeyClaims = JSON.stringify({
  access_token: {
    xms_cc: { values: ["cp1"] },
    acrs: { essential: true, value: passkeyMfaAuthContext }
  }
});
const msal = authConfigured
  ? new PublicClientApplication({
      auth: {
        clientId: config.clientId,
        authority: `https://${authorityHost}/${config.tenantId}`,
        knownAuthorities: [authorityHost],
        authorityMetadata,
        clientCapabilities: ["CP1"],
        redirectUri: hostedRedirectUri,
        postLogoutRedirectUri: hostedRedirectUri
      },
      cache: {
        cacheLocation: "localStorage",
        temporaryCacheLocation: "localStorage"
      }
    })
  : null;

let account = null;
let authMode = null;
let products = [];
let cart = JSON.parse(localStorage.getItem("beerstore-cart") ?? "[]");
let nativeCodeLoaded = false;
let transferredNativeToken = null;
let transferredNativeAccount = null;

const elements = {
  hero: document.querySelector("#hero"),
  message: document.querySelector("#message"),
  catalog: document.querySelector("#catalog"),
  cart: document.querySelector("#cart"),
  orders: document.querySelector("#orders"),
  cartCount: document.querySelector("#cart-count"),
  checkout: document.querySelector("#checkout"),
  nativeAuth: document.querySelector("#native-auth"),
  signIn: document.querySelector("#sign-in"),
  hostedSignIn: document.querySelector("#hosted-sign-in"),
  signOut: document.querySelector("#sign-out"),
  signedInUser: document.querySelector("#signed-in-user"),
  checkoutForm: document.querySelector("#checkout-form"),
  profile: document.querySelector("#profile"),
  unsignedAccessToken: document.querySelector("#unsigned-access-token"),
  accessTokenHeader: document.querySelector("#access-token-header"),
  accessTokenPayload: document.querySelector("#access-token-payload"),
  nativeAuthContent: document.querySelector("#native-auth-content"),
  nativeAuthError: document.querySelector("#native-auth-error"),
  nativeSignInTab: document.querySelector("#native-sign-in-tab"),
  nativeSignUpTab: document.querySelector("#native-sign-up-tab"),
  nativeCodeContent: document.querySelector("#native-code-content"),
  passkeysLink: document.querySelector("#passkeys-link"),
  passkeyOriginNotice: document.querySelector("#passkey-origin-notice"),
  passkeyJourney: document.querySelector("#passkey-journey"),
  passkeySignInCard: document.querySelector("#passkey-sign-in-card"),
  passkeySignIn: document.querySelector("#passkey-sign-in"),
  passkeyManagement: document.querySelector("#passkey-management"),
  passkeyName: document.querySelector("#passkey-name"),
  addPasskey: document.querySelector("#add-passkey"),
  refreshPasskeys: document.querySelector("#refresh-passkeys"),
  passkeyList: document.querySelector("#passkey-list")
};

const nativeAuth = authConfigured && config.nativeAuthApiUrl
  ? new NativeAuthController(config, apiScope, {
      onPrompt: renderNativePrompt,
      onBusy: setNativeAuthBusy,
      onError: showNativeAuthError,
      onRedirectRequired: completeHostedFallback,
      onAuthenticated: accountData => {
        authMode = "native";
        account = null;
        updateAuthenticationUi();
        if (isPasskeyManagementOrigin && passkeyJourney) {
          showView("passkeys");
        } else {
          renderNativeSuccess(accountData.getAccount().username);
        }
      },
      onSignedOut: () => {
        authMode = null;
        updateAuthenticationUi();
        showView("catalog");
      }
    })
  : null;

initializePasskeyTokenHandoff();

async function initialize() {
  let authenticationError = null;

  if (msal) {
    try {
      await msal.initialize();
      const redirectResult = await msal.handleRedirectPromise();
      account = redirectResult?.account ?? msal.getAllAccounts()[0] ?? null;
      if (account) authMode = "hosted";
    } catch (error) {
      console.error(error);
      if (error?.errorCode === "no_token_request_cache_error") {
        window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.search}`);
        account = msal.getAllAccounts()[0] ?? null;
        if (account) authMode = "hosted";
      } else {
        authenticationError = error;
      }
    }
  }

  if (nativeAuth) {
    try {
      const nativeAccountAvailable = await nativeAuth.initialize();
      if (nativeAccountAvailable) authMode = "native";
      else nativeAuth.showInitial();
    } catch (error) {
      console.error(error);
      showNativeAuthError(createNativeErrorExperience(error));
    }
  } else {
    elements.nativeAuth.classList.add("hidden");
  }

  updateAuthenticationUi();
  updateCartUi();
  await loadCatalog();
  if (isPasskeyManagementOrigin && passkeyJourney === "signin" && !account && !authenticationError) {
    await hostedSignIn({ prompt: "login" });
    return;
  }
  if (isPasskeyManagementOrigin || window.location.hash === "#passkeys") {
    await showView("passkeys");
  }

  if (authenticationError) {
    showAuthenticationError(authenticationError);
  }
}

async function loadCatalog() {
  const response = await fetch("/api/catalog");
  if (!response.ok) {
    return showMessage("The demo catalog could not be loaded.", true);
  }

  products = await response.json();
  elements.catalog.innerHTML = products.map(product => `
    <article class="card">
      <span class="badge">${escapeHtml(product.style)}</span>
      <h3>${escapeHtml(product.name)}</h3>
      <p>${escapeHtml(product.description)}</p>
      <div class="meta"><span>${product.abv}% ABV</span><strong>$${product.price.toFixed(2)}</strong></div>
      <p>${product.quantityAvailable} available</p>
      <button class="button secondary add-product" data-product-id="${product.id}" ${product.quantityAvailable < 1 ? "disabled" : ""}>Add to cart</button>
    </article>`).join("");
}

function addToCart(productId) {
  const product = products.find(item => item.id === productId);
  if (!product) return;

  const existing = cart.find(item => item.productId === productId);
  if (existing) existing.quantity += 1;
  else cart.push({ productId, name: product.name, unitPrice: product.price, quantity: 1 });
  persistCart();
  showView("cart");
}

function removeFromCart(productId) {
  cart = cart.filter(item => item.productId !== productId);
  persistCart();
}

function persistCart() {
  localStorage.setItem("beerstore-cart", JSON.stringify(cart));
  updateCartUi();
}

function updateCartUi() {
  elements.cartCount.textContent = cart.reduce((sum, item) => sum + item.quantity, 0);
  elements.checkout.disabled = cart.length === 0;
  elements.cart.innerHTML = cart.length === 0
    ? "<p>Your cart is empty.</p>"
    : cart.map(item => `
        <div class="cart-row">
          <strong>${escapeHtml(item.name)}</strong>
          <span>${item.quantity} × $${item.unitPrice.toFixed(2)}</span>
          <strong>$${(item.quantity * item.unitPrice).toFixed(2)}</strong>
          <button class="button danger remove-product" data-product-id="${item.productId}">Remove</button>
        </div>`).join("");
}

async function getAccessToken() {
  const result = await getAccessTokenResult();
  return result?.accessToken ?? null;
}

async function getAccessTokenResult() {
  if (authMode === "native") return nativeAuth.getAccessToken();
  if (authMode === "native-transfer" && transferredNativeToken) {
    return { accessToken: transferredNativeToken };
  }
  if (!msal || !account) return null;
  return msal.acquireTokenSilent({ account, scopes: [apiScope] });
}

async function loadOrders() {
  const token = await getAccessToken();
  if (!token) return showView("native-auth");

  const response = await fetch("/api/orders", {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!response.ok) return showMessage("Orders could not be loaded.", true);

  const orders = await response.json();
  elements.orders.innerHTML = orders.length === 0
    ? "<p>No demo orders have been captured.</p>"
    : orders.map(order => `
        <article class="order-card">
          <strong>Order ${escapeHtml(order.id)}</strong>
          <span>${new Date(order.orderTimestamp).toLocaleString()}</span>
          <p>${escapeHtml(order.status)} · ${escapeHtml(order.customerEmail)}</p>
        </article>`).join("");
}

async function submitOrder(event) {
  event.preventDefault();
  const token = await getAccessToken();
  if (!token) return showView("native-auth");

  const response = await fetch("/api/orders", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({
      customerDisplayName: document.querySelector("#customer-name").value,
      customerEmail: document.querySelector("#customer-email").value,
      contactNotes: document.querySelector("#contact-notes").value,
      isOverLegalAge: document.querySelector("#age-confirmation").checked,
      items: cart.map(item => ({ productId: item.productId, quantity: item.quantity }))
    })
  });

  const body = await response.json();
  if (!response.ok) return showMessage(body.error ?? "Order capture failed.", true);

  cart = [];
  persistCart();
  elements.checkoutForm.reset();
  showMessage(`Demo order ${body.id} was captured. No payment was collected.`);
  await loadCatalog();
  showView("orders");
}

function updateAuthenticationUi() {
  const currentAccount = getCurrentAccount();
  const signedIn = Boolean(currentAccount);
  elements.nativeAuth.classList.toggle("hidden", signedIn);
  elements.signIn.classList.toggle("hidden", signedIn);
  elements.hostedSignIn.classList.toggle("hidden", signedIn);
  elements.signOut.classList.toggle("hidden", !signedIn);
  elements.signedInUser.classList.toggle("hidden", !signedIn);
  elements.signedInUser.textContent = currentAccount
    ? `Signed in as ${currentAccount.name ?? currentAccount.username}`
    : "";
  document.querySelectorAll(".authenticated").forEach(element => element.classList.toggle("hidden", !signedIn));
  document.querySelectorAll(".unauthenticated").forEach(element => element.classList.toggle("hidden", signedIn));
  elements.passkeysLink.classList.toggle("hidden", !passkeyManagementUrl);
  if (passkeyManagementUrl) {
    elements.passkeysLink.href = `${passkeyManagementUrl.href.replace(/\/$/, "")}/?passkey=manage#passkeys`;
  }
}

async function loadProfile() {
  const result = await getAccessTokenResult();
  if (!result) return showView("native-auth");

  const { payload } = decodeJwt(result.accessToken);
  const currentAccount = getCurrentAccount();
  const fields = [
    ["Authentication flow", ["native", "native-transfer"].includes(authMode) ? "Native authentication" : "Hosted browser"],
    ["Display name", payload.name ?? currentAccount.name],
    ["Email", payload.email ?? payload.preferred_username ?? payload.emails ?? currentAccount.username],
    ["Username", currentAccount.username],
    ["Object ID", payload.oid],
    ["Subject", payload.sub],
    ["Tenant ID", payload.tid ?? currentAccount.tenantId],
    ["Scopes", payload.scp],
    ["Authentication methods", payload.amr],
    ["Authentication context", payload.acrs],
    ["Issued", formatTokenTime(payload.iat)],
    ["Expires", formatTokenTime(payload.exp)]
  ].filter(([, value]) => value !== undefined && value !== null && value !== "");

  elements.profile.innerHTML = `<dl class="profile-grid">${fields.map(([label, value]) => `
    <dt>${escapeHtml(label)}</dt>
    <dd>${escapeHtml(formatClaim(value))}</dd>`).join("")}</dl>`;
}

async function loadDebug() {
  const result = await getAccessTokenResult();
  if (!result) return showView("native-auth");

  const decoded = decodeJwt(result.accessToken);
  elements.unsignedAccessToken.textContent = decoded.unsignedToken;
  elements.accessTokenHeader.textContent = JSON.stringify(decoded.header, null, 2);
  elements.accessTokenPayload.textContent = JSON.stringify(decoded.payload, null, 2);
}

async function hostedSignIn(options = {}) {
  if (!msal) return showMessage("External ID configuration is not available.", true);

  try {
    await msal.loginRedirect({
      scopes: ["openid", "profile", "email", apiScope],
      ...(isPasskeyManagementOrigin ? { claims: passkeyClaims } : {}),
      ...options
    });
  } catch (error) {
    console.error(error);
    showAuthenticationError(error);
  }
}

async function startPasskeyJourney(mode = "signin") {
  if (!passkeyManagementUrl) {
    return showMessage("Passkey authentication is not configured.", true);
  }

  const url = new URL(passkeyManagementUrl);
  url.searchParams.set("passkey", mode);
  url.hash = "passkeys";
  if (!isPasskeyManagementOrigin && nativeAuth?.isSignedIn && mode !== "signin") {
    const passkeyWindow = window.open(url, "bulldog-pints-passkeys");
    if (!passkeyWindow) {
      window.location.assign(url);
      return;
    }

    const tokenPromise = nativeAuth.getAccessToken();
    const receiveReady = async event => {
      if (event.origin !== passkeyManagementUrl.origin ||
          event.source !== passkeyWindow ||
          event.data?.type !== "beerstore-passkey-ready") {
        return;
      }
      const tokenResult = await tokenPromise;
      passkeyWindow.postMessage({
        type: "beerstore-native-token",
        accessToken: tokenResult?.accessToken ?? tokenResult
      }, passkeyManagementUrl.origin);
      window.removeEventListener("message", receiveReady);
    };
    window.addEventListener("message", receiveReady);
    return;
  }
  window.location.assign(url);
}

function initializePasskeyTokenHandoff() {
  if (!isPasskeyManagementOrigin || !passkeyJourney || !window.opener) return;

  const storefrontOrigin = new URL(config.redirectUri).origin;
  const receiveToken = event => {
    if (event.origin !== storefrontOrigin ||
        event.source !== window.opener ||
        event.data?.type !== "beerstore-native-token" ||
        typeof event.data.accessToken !== "string") {
      return;
    }

    const payload = decodeJwt(event.data.accessToken).payload;
    transferredNativeToken = event.data.accessToken;
    transferredNativeAccount = {
      username: payload.email ?? payload.preferred_username ?? payload.sub,
      name: payload.name,
      tenantId: payload.tid
    };
    authMode = "native-transfer";
    account = null;
    window.removeEventListener("message", receiveToken);
    updateAuthenticationUi();
    showView("passkeys");
  };
  window.addEventListener("message", receiveToken);

  window.opener.postMessage(
    { type: "beerstore-passkey-ready" },
    storefrontOrigin
  );
}

async function completeHostedFallback(username) {
  if (!msal) throw new Error("Hosted authentication is not configured.");

  showNativeAuthError(null);
  elements.nativeAuthContent.innerHTML = `
    <div class="native-auth-success">
      <h3>Additional verification required</h3>
      <p>Continue in the secure sign-in window to register or complete MFA.</p>
    </div>`;

  const result = await msal.loginPopup({
    authority: `https://${authorityHost}/${config.tenantId}`,
    scopes: ["openid", "profile", "email", apiScope],
    loginHint: username,
    prompt: "login"
  });

  account = result.account;
  authMode = "hosted";
  renderHostedFallbackSuccess(account.username);
  updateAuthenticationUi();
}

async function signOut() {
  if (authMode === "native") {
    await nativeAuth.signOut();
    nativeAuth.showInitial();
  } else if (authMode === "native-transfer") {
    transferredNativeToken = null;
    transferredNativeAccount = null;
    authMode = null;
    updateAuthenticationUi();
    await loadPasskeys();
  } else if (msal && account) {
    await msal.logoutRedirect({ account });
  }
}

async function showView(viewName) {
  elements.hero.classList.add("hidden");
  document.querySelectorAll(".view").forEach(view => view.classList.add("hidden"));
  document.querySelector(`#${viewName}-view`)?.classList.remove("hidden");
  if (viewName === "orders") await loadOrders();
  if (viewName === "profile") await loadProfile();
  if (viewName === "debug") await loadDebug();
  if (viewName === "native-code") await loadNativeCode();
  if (viewName === "passkeys") await loadPasskeys();
}

async function loadPasskeys() {
  if (!passkeyManagementUrl) {
    elements.passkeyOriginNotice.textContent = "Passkey management is not configured.";
    elements.passkeyOriginNotice.classList.remove("hidden");
    elements.passkeySignInCard.classList.add("hidden");
    elements.passkeyManagement.classList.add("hidden");
    elements.passkeyJourney.classList.add("hidden");
    return;
  }

  if (!isPasskeyManagementOrigin) {
    window.location.assign(`${passkeyManagementUrl.href.replace(/\/$/, "")}/#passkeys`);
    return;
  }

  if (!["native", "native-transfer"].includes(authMode) || !getCurrentAccount()) {
    elements.passkeySignInCard.classList.remove("hidden");
    elements.passkeyManagement.classList.add("hidden");
    renderPasskeyJourney(null);
    return;
  }

  elements.passkeySignInCard.classList.add("hidden");
  elements.passkeyManagement.classList.remove("hidden");
  elements.passkeyList.innerHTML = "<p>Loading passkeys...</p>";

  try {
    const token = await getPasskeyAccessToken();
    const response = await fetch("/api/passkeys", {
      headers: { Authorization: `Bearer ${token}` }
    });
    const body = await readApiResponse(response);
    if (!response.ok) throw createApiError(body, response.status);
    const passkeys = body.value ?? [];
    renderPasskeys(passkeys);
    renderPasskeyJourney(passkeys);
  } catch (error) {
    console.error(error);
    elements.passkeyList.innerHTML =
      `<p class="native-auth-error">${escapeHtml(formatErrorDetails(error))}</p>`;
  }
}

async function registerPasskey() {
  if (!window.PublicKeyCredential || !navigator.credentials?.create) {
    return showMessage("This browser does not support passkeys.", true);
  }

  const displayName = elements.passkeyName.value.trim();
  if (!displayName) return showMessage("Enter a name for this passkey.", true);

  setPasskeyBusy(true);
  try {
    let token = await getPasskeyAccessToken();
    let { response: optionsResponse, body: optionsBody } =
      await requestPasskeyCreationOptions(token);
    if (requiresMfaStepUp(optionsResponse, optionsBody)) {
      token = await getHostedMfaToken(token);
      ({ response: optionsResponse, body: optionsBody } =
        await requestPasskeyCreationOptions(token));
    }
    if (!optionsResponse.ok) throw createApiError(optionsBody, optionsResponse.status);

    const creationOptions = optionsBody.publicKey;
    const extensions = { ...creationOptions.extensions };
    if (creationOptions.authenticatorSelection?.userVerification === "required" &&
        extensions.enforceCredentialProtectionPolicy &&
        extensions.credentialProtectionPolicy !== "userVerificationRequired") {
      // Graph can return an optional credential-protection policy while requiring UV.
      extensions.credentialProtectionPolicy = "userVerificationRequired";
    }
    const publicKey = {
      ...creationOptions,
      challenge: base64UrlToBuffer(creationOptions.challenge),
      rp: {
        ...creationOptions.rp,
        id: config.passkeyRpId || creationOptions.rp.id
      },
      user: {
        ...creationOptions.user,
        id: base64UrlToBuffer(creationOptions.user.id)
      },
      excludeCredentials: (creationOptions.excludeCredentials ?? []).map(credential => ({
        ...credential,
        id: decodeGraphCredentialId(credential.id)
      })),
      extensions
    };
    const credential = await navigator.credentials.create({ publicKey });
    if (!credential) throw new Error("The browser did not return a passkey credential.");

    const registration = {
      displayName,
      publicKeyCredential: {
        id: credential.id,
        type: credential.type,
        response: {
          attestationObject: bufferToBase64Url(credential.response.attestationObject),
          clientDataJSON: bufferToBase64Url(credential.response.clientDataJSON)
        }
      }
    };
    let { response, body } = await requestPasskeyRegistration(registration, token);
    if (requiresMfaStepUp(response, body)) {
      token = await getHostedMfaToken(token);
      ({ response, body } = await requestPasskeyRegistration(registration, token));
    }
    if (!response.ok) throw createApiError(body, response.status);

    showMessage(`${displayName} was added. You can now use it for hosted sign-in.`);
    const url = new URL(window.location.href);
    url.searchParams.set("passkey", "manage");
    window.history.replaceState({}, "", url);
    await loadPasskeys();
  } catch (error) {
    console.error(error);
    if (error.name !== "NotAllowedError") {
      showMessage(`Passkey registration failed.\n\n${formatErrorDetails(error)}`, true);
    }
  } finally {
    setPasskeyBusy(false);
  }
}

async function removePasskey(passkeyId, displayName) {
  if (!window.confirm(`Remove ${displayName}? This passkey will stop working immediately.`)) {
    return;
  }

  setPasskeyBusy(true);
  try {
    let token = await getPasskeyAccessToken();
    let { response, body } = await requestPasskeyRemoval(passkeyId, token);
    if (requiresMfaStepUp(response, body)) {
      token = await getHostedMfaToken(token);
      ({ response, body } = await requestPasskeyRemoval(passkeyId, token));
    }
    if (!response.ok) throw createApiError(body, response.status);

    showMessage(`${displayName} was removed.`);
    await loadPasskeys();
  } catch (error) {
    console.error(error);
    showMessage(`Passkey removal failed.\n\n${formatErrorDetails(error)}`, true);
  } finally {
    setPasskeyBusy(false);
  }
}

async function getPasskeyAccessToken() {
  if (authMode === "native" && nativeAuth?.isSignedIn) {
    const result = await nativeAuth.getAccessToken();
    return result?.accessToken ?? result;
  }
  if (authMode === "native-transfer" && transferredNativeToken) {
    return transferredNativeToken;
  }

  throw new Error("Native authentication is required for passkey management.");
}

async function getHostedMfaToken(nativeToken) {
  if (!msal) throw new Error("Hosted MFA is not configured.");
  const result = await msal.loginPopup({
    scopes: ["openid", "profile", "email", apiScope],
    claims: passkeyClaims,
    loginHint: getCurrentAccount()?.username,
  });
  assertSamePasskeyAccount(nativeToken, result.accessToken);
  return result.accessToken;
}

async function requestPasskeyCreationOptions(token) {
  const response = await fetch("/api/passkeys/creation-options", {
    headers: { Authorization: `Bearer ${token}` }
  });
  return { response, body: await readApiResponse(response) };
}

async function requestPasskeyRemoval(passkeyId, token) {
  const response = await fetch(`/api/passkeys/${encodeURIComponent(passkeyId)}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` }
  });
  return { response, body: await readApiResponse(response) };
}

async function requestPasskeyRegistration(registration, token) {
  const response = await fetch("/api/passkeys", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(registration)
  });
  return { response, body: await readApiResponse(response) };
}

function requiresMfaStepUp(response, body) {
  return response.status === 403 && body?.error === "fresh_mfa_required";
}

function assertSamePasskeyAccount(nativeToken, hostedToken) {
  const nativeClaims = decodeJwt(nativeToken).payload;
  const hostedClaims = decodeJwt(hostedToken).payload;
  const nativeId = nativeClaims.oid ?? nativeClaims.sub;
  const hostedId = hostedClaims.oid ?? hostedClaims.sub;
  if (!nativeId || !hostedId ||
      nativeId !== hostedId ||
      (nativeClaims.tid && hostedClaims.tid && nativeClaims.tid !== hostedClaims.tid)) {
    throw new Error("MFA must be completed with the same account used for native authentication.");
  }
}

function renderPasskeys(passkeys) {
  elements.passkeyList.className = "passkey-list";
  elements.passkeyList.innerHTML = passkeys.length === 0
    ? "<p>No passkeys are registered yet.</p>"
    : passkeys.map(passkey => `
      <article class="passkey-item">
        <div>
          <h4>${escapeHtml(passkey.displayName || "Unnamed passkey")}</h4>
          <p class="muted">
            ${escapeHtml(passkey.model || passkey.passkeyType || "Passkey")}
            · Added ${escapeHtml(formatDate(passkey.createdDateTime))}
            ${passkey.lastUsedDateTime ? ` · Last used ${escapeHtml(formatDate(passkey.lastUsedDateTime))}` : ""}
          </p>
        </div>
        <button
          class="button danger delete-passkey"
          type="button"
          data-passkey-id="${escapeHtml(passkey.id)}"
          data-passkey-name="${escapeHtml(passkey.displayName || "this passkey")}">Remove</button>
      </article>`).join("");
}

function renderPasskeyJourney(passkeys) {
  const journey = new URLSearchParams(window.location.search).get("passkey");
  if (!journey || !isPasskeyManagementOrigin) {
    elements.passkeyJourney.classList.add("hidden");
    return;
  }

  elements.passkeyJourney.classList.remove("hidden");
  if (!passkeys) {
    elements.passkeyJourney.innerHTML = `
      <p class="eyebrow">Secure handoff</p>
      <h3>Continue with native authentication</h3>
      <p>Sign in inside Bulldog Pints to securely manage the passkeys for your account.</p>`;
    return;
  }

  const storefrontUrl = new URL(config.redirectUri).origin;
  if (journey === "setup" && passkeys.length === 0) {
    elements.passkeyJourney.innerHTML = `
      <p class="eyebrow">One last step</p>
      <h3>Create your first passkey</h3>
      <p>Add one passkey below. Next time, choose <strong>Passkey sign in</strong> instead of entering your password.</p>`;
    elements.passkeyName.focus();
    return;
  }

  if (passkeys.length === 0) {
    elements.passkeyJourney.innerHTML = `
      <p class="eyebrow">Passkey management</p>
      <h3>No passkeys are registered yet</h3>
      <p>Add your first passkey below to enable phishing-resistant sign-in.</p>`;
    return;
  }

  elements.passkeyJourney.innerHTML = `
    <p class="eyebrow">Passkey ready</p>
    <h3>${passkeys.length === 1 ? "Your passkey is ready" : `${passkeys.length} passkeys are ready`}</h3>
    <p>You can sign in with a registered passkey and return here whenever you need to add or remove one.</p>
    <a class="button primary journey-link" href="${escapeHtml(storefrontUrl)}">Continue to Bulldog Pints</a>`;
}

function setPasskeyBusy(isBusy) {
  elements.addPasskey.disabled = isBusy;
  elements.refreshPasskeys.disabled = isBusy;
  elements.passkeyList.querySelectorAll("button").forEach(button => {
    button.disabled = isBusy;
  });
}

function base64UrlToBuffer(value) {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/")
    .padEnd(Math.ceil(value.length / 4) * 4, "=");
  return Uint8Array.from(atob(base64), character => character.charCodeAt(0)).buffer;
}

function bufferToBase64Url(value) {
  const bytes = new Uint8Array(value);
  let binary = "";
  bytes.forEach(byte => { binary += String.fromCharCode(byte); });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function decodeGraphCredentialId(value) {
  const match = value.match(/^(.*?)(\d)$/);
  if (!match) return base64UrlToBuffer(value);
  return base64UrlToBuffer(`${match[1]}${"=".repeat(Number(match[2]))}`);
}

async function readApiResponse(response) {
  if (response.status === 204) return null;
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

function createApiError(body, status) {
  const error = new Error(body?.message ?? `The passkey API returned status ${status}.`);
  error.errorCode = body?.error;
  error.details = body?.details;
  return error;
}

function formatDate(value) {
  return value ? new Date(value).toLocaleString() : "unknown";
}

function showMessage(text, isError = false) {
  elements.message.textContent = text;
  elements.message.style.background = isError ? "#9e2a2b" : "#275d38";
  elements.message.classList.remove("hidden");
}

function showAuthenticationError(error) {
  showMessage(`Sign-in failed.\n\n${formatErrorDetails(error)}`, true);
}

async function loadNativeCode() {
  if (nativeCodeLoaded) return;

  const files = [
    ["Native flow controller", "/source/native-auth.js.txt"]
  ];

  try {
    const sources = await Promise.all(files.map(async ([title, path]) => {
      const response = await fetch(path);
      if (!response.ok) throw new Error(`Could not load ${path}.`);
      return [title, path, await response.text()];
    }));

    elements.nativeCodeContent.innerHTML = sources.map(([title, path, source]) => `
      <details class="card native-code-file" open>
        <summary>${escapeHtml(title)} <code>${escapeHtml(path)}</code></summary>
        <pre><code>${escapeHtml(source)}</code></pre>
      </details>`).join("");
    nativeCodeLoaded = true;
  } catch (error) {
    elements.nativeCodeContent.innerHTML = `<p class="native-auth-error">${escapeHtml(formatErrorDetails(error))}</p>`;
  }
}

function getCurrentAccount() {
  if (authMode === "native") return nativeAuth.account;
  if (authMode === "native-transfer") return transferredNativeAccount;
  return account;
}

function renderNativePrompt(prompt) {
  elements.nativeSignInTab.classList.toggle("active", prompt.mode === "sign-in");
  elements.nativeSignUpTab.classList.toggle("active", prompt.mode === "sign-up");
  showNativeAuthError(null);

  elements.nativeAuthContent.innerHTML = `
    <form id="native-auth-form" class="native-auth-form">
      <h3>${escapeHtml(prompt.title)}</h3>
      <p class="muted">${escapeHtml(prompt.description)}</p>
      ${prompt.fields.map(field => `
        <label>${escapeHtml(field.label)}
          <input
            name="${escapeHtml(field.name)}"
            type="${escapeHtml(field.type ?? "text")}"
            autocomplete="${escapeHtml(field.autocomplete ?? "off")}"
            inputmode="${escapeHtml(field.inputMode ?? "text")}"
            value="${escapeHtml(field.value ?? "")}"
            required>
        </label>`).join("")}
      <button class="button primary" type="submit">${escapeHtml(prompt.submitLabel)}</button>
    </form>`;

  document.querySelector("#native-auth-form").addEventListener("submit", async event => {
    event.preventDefault();
    const values = Object.fromEntries(new FormData(event.currentTarget));
    await nativeAuth.submit(values);
  });
}

function renderNativeSuccess(username) {
  showNativeAuthError(null);
  elements.nativeAuthContent.innerHTML = `
    <div class="native-auth-success">
      <h3>Welcome to the pack</h3>
      <p>Signed in as ${escapeHtml(username)} using native authentication.</p>
      <p><strong>Protect your account with a passkey.</strong> Your current native session will be reused. Additional MFA is requested only when the token does not contain recent MFA proof.</p>
      <div class="native-auth-actions">
        <button class="button primary" type="button" data-passkey-action="setup">Set up a passkey</button>
        <button class="button secondary" type="button" data-view="catalog">Not now</button>
      </div>
    </div>`;
}

function renderHostedFallbackSuccess(username) {
  showNativeAuthError(null);
  elements.nativeAuthContent.innerHTML = `
    <div class="native-auth-success">
      <h3>Verification complete</h3>
      <p>Signed in as ${escapeHtml(username)} after completing secure browser verification.</p>
      <button class="button primary" type="button" data-view="catalog">Browse beers</button>
    </div>`;
}

function setNativeAuthBusy(isBusy) {
  const button = elements.nativeAuthContent.querySelector('button[type="submit"]');
  if (button) button.disabled = isBusy;
}

function showNativeAuthError(errorExperience) {
  elements.nativeAuthError.classList.toggle("hidden", !errorExperience);
  if (!errorExperience) {
    elements.nativeAuthError.replaceChildren();
    return;
  }

  elements.nativeAuthError.innerHTML = `
    <div class="native-auth-error-heading">
      <span class="native-auth-error-icon" aria-hidden="true">!</span>
      <div>
        <h4>${escapeHtml(errorExperience.title)}</h4>
        <p>${escapeHtml(errorExperience.message)}</p>
      </div>
    </div>
    <details class="native-auth-error-details">
      <summary>Show technical details</summary>
      <pre><code>${escapeHtml(errorExperience.details)}</code></pre>
    </details>`;
}

function formatErrorDetails(error) {
  if (!error || typeof error !== "object") return String(error);

  const details = [];
  const addDetail = (label, value) => {
    if (value !== undefined && value !== null && value !== "") {
      details.push(`${label}: ${typeof value === "object" ? JSON.stringify(value) : value}`);
    }
  };

  addDetail("Message", error.message ?? error.errorMessage);
  addDetail("Error code", error.errorCode);
  addDetail("Suberror", error.subError);
  addDetail("Correlation ID", error.correlationId);
  addDetail("Trace ID", error.traceId);
  addDetail("Timestamp", error.timestamp);
  addDetail("Details", error.details);

  return details.length > 0 ? details.join("\n") : String(error);
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, character => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#039;"
  })[character]);
}

function decodeJwt(token) {
  const segments = token.split(".");
  if (segments.length < 2) throw new Error("The access token is not a JWT.");

  return {
    unsignedToken: `${segments[0]}.${segments[1]}`,
    header: decodeJwtSegment(segments[0]),
    payload: decodeJwtSegment(segments[1])
  };
}

function decodeJwtSegment(segment) {
  const base64 = segment.replace(/-/g, "+").replace(/_/g, "/")
    .padEnd(Math.ceil(segment.length / 4) * 4, "=");
  const bytes = Uint8Array.from(atob(base64), character => character.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
}

function formatClaim(value) {
  return Array.isArray(value)
    ? value.join(", ")
    : typeof value === "object"
      ? JSON.stringify(value)
      : String(value);
}

function formatTokenTime(value) {
  return typeof value === "number"
    ? new Date(value * 1000).toLocaleString()
    : value;
}

document.addEventListener("click", event => {
  const viewButton = event.target.closest("[data-view]");
  if (viewButton) showView(viewButton.dataset.view);

  const addButton = event.target.closest(".add-product");
  if (addButton) addToCart(addButton.dataset.productId);

  const removeButton = event.target.closest(".remove-product");
  if (removeButton) removeFromCart(removeButton.dataset.productId);

  const deletePasskeyButton = event.target.closest(".delete-passkey");
  if (deletePasskeyButton) {
    removePasskey(
      deletePasskeyButton.dataset.passkeyId,
      deletePasskeyButton.dataset.passkeyName
    );
  }

  const passkeyAction = event.target.closest("[data-passkey-action]");
  if (passkeyAction) startPasskeyJourney(passkeyAction.dataset.passkeyAction);

  const authAction = event.target.closest("[data-auth-action]");
  if (authAction?.dataset.authAction === "native") showView("native-auth");
  if (authAction?.dataset.authAction === "passkey") startPasskeyJourney("signin");
  if (authAction?.dataset.authAction === "web") hostedSignIn();
});

elements.checkout.addEventListener("click", () => getCurrentAccount() ? showView("checkout") : showView("native-auth"));
elements.checkoutForm.addEventListener("submit", submitOrder);
elements.nativeAuth.addEventListener("click", () => showView("native-auth"));
elements.nativeSignInTab.addEventListener("click", () => nativeAuth?.showInitial("sign-in"));
elements.nativeSignUpTab.addEventListener("click", () => nativeAuth?.showInitial("sign-up"));
elements.signIn.addEventListener("click", () => startPasskeyJourney("signin"));
elements.hostedSignIn.addEventListener("click", () => hostedSignIn());
elements.signOut.addEventListener("click", signOut);
elements.passkeysLink.addEventListener("click", event => {
  if (!nativeAuth?.isSignedIn || isPasskeyManagementOrigin) return;
  event.preventDefault();
  startPasskeyJourney("manage");
});
elements.passkeySignIn.addEventListener("click", () => {
  nativeAuth?.showInitial("sign-in");
  showView("native-auth");
});
elements.addPasskey.addEventListener("click", registerPasskey);
elements.refreshPasskeys.addEventListener("click", loadPasskeys);

initialize().catch(error => {
  console.error(error);
  showMessage("The demo could not be initialized.", true);
});
